import React from 'react'
import { fetchSignalsForOpportunity, type OpportunitySignals } from '../services/signalsService'

const SIGNALS_CACHE_TTL_MS = 5 * 60 * 1000

type CacheEntry = {
  value: OpportunitySignals | null
  at: number
}

const signalsCache = new Map<number, CacheEntry>()

function getCachedSignals(id: number): OpportunitySignals | null | undefined {
  const entry = signalsCache.get(id)
  if (!entry) return undefined
  if (Date.now() - entry.at > SIGNALS_CACHE_TTL_MS) {
    signalsCache.delete(id)
    return undefined
  }
  return entry.value
}

async function loadSignals(id: number): Promise<OpportunitySignals | null> {
  const existing = getCachedSignals(id)
  if (existing !== undefined) {
    return existing
  }
  const result = await fetchSignalsForOpportunity(id)
  signalsCache.set(id, { value: result, at: Date.now() })
  return result
}

export function useOpportunitySignals(
  opportunityId: number | string | null | undefined,
  enabled: boolean,
) {
  const [signals, setSignals] = React.useState<OpportunitySignals | null>(null)
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    let mounted = true

    if (!enabled) {
      setSignals(null)
      setLoading(false)
      return () => {
        mounted = false
      }
    }

    const numericId =
      typeof opportunityId === 'number'
        ? opportunityId
        : Number(String(opportunityId ?? '').trim())

    if (!Number.isFinite(numericId)) {
      setSignals(null)
      setLoading(false)
      return () => {
        mounted = false
      }
    }

    const cached = getCachedSignals(numericId)
    if (cached !== undefined) {
      setSignals(cached)
      setLoading(false)
      return () => {
        mounted = false
      }
    }

    setLoading(true)

    loadSignals(numericId)
      .then((result) => {
        if (!mounted) return
        setSignals(result)
      })
      .catch(() => {
        if (!mounted) return
        setSignals(null)
      })
      .finally(() => {
        if (!mounted) return
        setLoading(false)
      })

    return () => {
      mounted = false
    }
  }, [opportunityId, enabled])

  return { signals, loading }
}
